import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  getClientPriceListForClient,
  respondToClientPriceList,
  ClientPriceListClientView,
  ClientPriceListClientItem,
} from '../lib/clientPriceListApi';
import LoadingState from '../components/LoadingState';

// Section A — Client-facing response page (A1-A5).
// The client opens this URL (handed to them by USC), edits ONLY Qty + Client Remarks,
// sees grand totals + section totals update live, and submits the form. No auth.
//
// Layout follows the spec: 8 columns (UOM / Description / Unit / Qty Ordered /
// Unit Selling Price USD / Extension USD / Our Remarks / Client's Remarks),
// 3 sections (Provision / Cabin / Additional), totals at top + per-section + grand.

const SECTION_TITLES: Record<string, string> = {
  provision: 'Provision Stores',
  cabin: 'Cabin Stores',
  additional: 'Additional Items Required',
};

const SECTION_ORDER = ['provision', 'cabin', 'additional'] as const;
type SectionKey = typeof SECTION_ORDER[number];

interface DraftRow {
  quantity: number;
  client_remarks: string;
}

// D116.1 (bug #33): the link carries a secret (`?t=`); the server answers a wrong
// or missing one, and a reply to a list that is already an order, with a code.
// The client switches on the code, never on the server's prose.
export const LINK_INVALID_TEXT = 'This link is not valid. Ask us to send it again.';
export const LINK_LOCKED_TEXT = 'This list has already been turned into an order, so it can no longer be changed.';

function errorText(e: any, fallback: string) {
  if (e?.code === 'CLIENT_PRICE_LIST_LINK_INVALID') return LINK_INVALID_TEXT;
  if (e?.code === 'CLIENT_PRICE_LIST_LOCKED') return LINK_LOCKED_TEXT;
  return e?.message || fallback;
}

function fmtMoney(n: number) {
  return `US$ ${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
}

export default function ClientPriceListResponsePage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const [list, setList] = useState<ClientPriceListClientView | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<DraftRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    if (!token) {
      // A link with no secret opens nothing; the server would say the same.
      setError(LINK_INVALID_TEXT);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        // The public client view only (D112.2): no cost, markup or internal remarks.
        const res = await getClientPriceListForClient(id, token);
        const doc = res.data ?? null;
        setList(doc);
        if (doc?.items) {
          setDraft(doc.items.map((it: ClientPriceListClientItem) => ({
            quantity: Number(it.quantity || 0),
            client_remarks: String(it.client_remarks || ''),
          })));
        }
      } catch (e: any) {
        setError(errorText(e, 'Failed to load price list'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token]);

  // A3/A5 — live totals derived from draft state (NOT from server)
  const totals = useMemo(() => {
    const sections: Record<SectionKey, { qty: number; selling: number }> = {
      provision: { qty: 0, selling: 0 },
      cabin: { qty: 0, selling: 0 },
      additional: { qty: 0, selling: 0 },
    };
    let grandQty = 0;
    let grandSelling = 0;
    (list?.items || []).forEach((it, idx) => {
      const sec = (SECTION_ORDER as readonly string[]).includes(it.section || '')
        ? (it.section as SectionKey)
        : 'provision';
      const qty = Number(draft[idx]?.quantity || 0);
      const price = Number(it.unit_price || 0);
      const ext = qty * price;
      sections[sec].qty += qty;
      sections[sec].selling += ext;
      grandQty += qty;
      grandSelling += ext;
    });
    return { sections, grand: { qty: grandQty, selling: grandSelling } };
  }, [list, draft]);

  // Group items by section in display order
  const grouped = useMemo(() => {
    const out: Record<SectionKey, Array<{ item: ClientPriceListClientItem; index: number }>> = {
      provision: [],
      cabin: [],
      additional: [],
    };
    (list?.items || []).forEach((it, idx) => {
      const sec = (SECTION_ORDER as readonly string[]).includes(it.section || '')
        ? (it.section as SectionKey)
        : 'provision';
      out[sec].push({ item: it, index: idx });
    });
    return out;
  }, [list]);

  const updateRow = (idx: number, patch: Partial<DraftRow>) => {
    setDraft((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const submit = async () => {
    if (!id) return;
    setSubmitting(true);
    setError(null);
    try {
      await respondToClientPriceList(id, token, {
        items: draft.map((d) => ({ quantity: d.quantity, client_remarks: d.client_remarks })),
      });
      setSubmitted(true);
    } catch (e: any) {
      setError(errorText(e, 'Failed to submit response'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingState />;

  if (!list) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error || 'Price list not found.'}
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-6 py-8 text-center">
          <div className="text-2xl mb-2">✓</div>
          <div className="text-lg font-semibold text-emerald-900">Thank you — your order has been submitted.</div>
          <div className="text-sm text-emerald-700 mt-2">
            United Ship Chandlers will confirm shortly. Grand Total: <strong>{fmtMoney(totals.grand.selling)}</strong>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-8 bg-white min-h-screen">
      {/* Header */}
      <div className="border-b border-slate-200 pb-4 mb-6">
        <div className="text-xs text-primary-500 font-semibold uppercase tracking-wide">United Ship Chandlers</div>
        <h1 className="text-xl font-semibold leading-7 text-slate-900 mt-1">{list.name}</h1>
        <div className="text-xs text-slate-500 mt-1">
          Vessel: <strong>{list.vesselName || '—'}</strong> • Prepared for: <strong>{list.clientName || '—'}</strong>
        </div>
      </div>

      {/* A3 — Grand Total at top */}
      <div className="sticky top-0 z-10 bg-primary-500 text-white rounded-md px-4 py-3 mb-6 shadow-md flex items-center justify-between">
        <div>
          <div className="text-xs uppercase opacity-80">Grand Total</div>
          <div className="text-2xl font-bold">{fmtMoney(totals.grand.selling)}</div>
        </div>
        <div className="text-right text-xs">
          <div>Total Items: <strong>{totals.grand.qty}</strong></div>
          <div className="opacity-80 mt-1">Updates live as you enter quantities</div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      {/* A4 — 3 sections */}
      {SECTION_ORDER.map((section) => {
        const rows = grouped[section];
        if (rows.length === 0) return null;
        return (
          <div key={section} className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-slate-900">{SECTION_TITLES[section]}</h2>
              <div className="text-sm text-slate-600">
                Subtotal: <strong className="text-primary-500">{fmtMoney(totals.sections[section].selling)}</strong>
                <span className="text-xs text-slate-500 ml-2">({totals.sections[section].qty} items)</span>
              </div>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              {/* Pack and description stay put while price, quantity and the
                  two remark columns scroll (`frozen-cols-*`, index.css). 176px
                  is the UOM column's own `w-44`. */}
              <table
                className="w-full text-xs frozen-cols-2"
                style={{ '--frozen-col-2': '176px' } as React.CSSProperties}
              >
                <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                  <tr>
                    {/* Widened for TECH_DEBT #157: this column used to hold a single unit
   name ("CARTON"). It now describes the pack — "CARTON of 12 x 794 G" —
   while the "Unit" column beside it names what the price, and so the
   quantity being asked for, is per. */}
                    <th className="px-3 py-2 text-left w-44 bg-slate-50">UOM</th>
                    <th className="px-3 py-2 text-left bg-slate-50">Description</th>
                    <th className="px-3 py-2 text-left w-24">Unit</th>
                    <th className="px-3 py-2 text-center w-28 bg-slate-50">Qty Ordered</th>
                    <th className="px-3 py-2 text-right w-32">Unit Selling Price (US$)</th>
                    <th className="px-3 py-2 text-right w-32">Extension (US$)</th>
                    <th className="px-3 py-2 text-left w-44">Our Remarks</th>
                    <th className="px-3 py-2 text-left w-44 bg-slate-50">Client&apos;s Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ item, index }) => {
                    const qty = Number(draft[index]?.quantity || 0);
                    const price = Number(item.unit_price || 0);
                    const ext = qty * price;
                    return (
                      <tr key={index} className="border-t border-slate-100 align-top">
                        <td className="px-3 py-2 w-44 bg-white text-slate-700">{item.uom || ''}</td>
                        <td className="px-3 py-2 bg-white text-slate-800">{item.description}</td>
                        <td className="px-3 py-2 text-slate-700">{item.packaging || ''}</td>
                        <td className="px-3 py-2 bg-amber-50/50">
                          <input
                            type="number"
                            min={0}
                            step="1"
                            value={qty}
                            onChange={(e) => updateRow(index, { quantity: Math.max(0, Number(e.target.value) || 0) })}
                            className="w-20 h-8 px-2 rounded border border-slate-300 text-center"
                          />
                        </td>
                        <td className="px-3 py-2 text-right text-slate-700 tabular-nums">{fmtMoney(price)}</td>
                        <td className="px-3 py-2 text-right text-slate-900 font-semibold tabular-nums">
                          {fmtMoney(ext)}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{item.remarks || ''}</td>
                        <td className="px-3 py-2 bg-amber-50/50">
                          <input
                            type="text"
                            value={draft[index]?.client_remarks || ''}
                            onChange={(e) => updateRow(index, { client_remarks: e.target.value })}
                            placeholder="Add a note..."
                            className="w-full h-8 px-2 rounded border border-slate-300 text-xs"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Submit */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 pt-4 pb-2 flex items-center justify-between">
        <div className="text-sm text-slate-600">
          Once submitted, USC will review and confirm your order. Grand Total:{' '}
          <strong className="text-primary-500">{fmtMoney(totals.grand.selling)}</strong>
        </div>
        <button
          onClick={submit}
          disabled={submitting || totals.grand.qty === 0}
          className="px-6 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Submit Order'}
        </button>
      </div>
    </div>
  );
}
